import { useMemo, useState } from "react";

import { Box, Button, Space } from "@mantine/core";
import { BottomModalSection } from "~/../../base-components/lib/modal/ModalSection";
import { networkLookup } from "~/../../shared-features/lib/utils/blockExplorer";

import {
  CollectionContractType,
  Condition,
  ConditionType,
  ContractNft,
  _CollectionType,
} from "apollo/graphql.generated";
import { useContractDetailsQuery } from "~/features/gates/queries/gates.graphql.generated";
import { GetCollectionByUserPaginatedQuery } from "~/features/tokens/queries/collections.graphql.generated";

import ContractCollectionAttributeGrid, {
  TokenGridAttribute,
} from "../../grids/ContractCollectionAttributeGrid";
import { ContractCollectionTokenGrid } from "../../grids/ContractCollectionTokenGrid";
import HighlightCollectionGrid from "../../grids/HighlighCollectionGrid";
import { SimpleInfiniteListItem } from "../../grids/SimpleInfiniteGrid";
import CollectionDetailsSection, {
  CustomCollectionDetails,
} from "../sections/CollectionDetailsSection";

import { TokenDetails } from "./ByContractOrUrlTab";

export type HighlightCollections =
  GetCollectionByUserPaginatedQuery["getCollectionByUserPaginated"]["collections"];

export const pickSeriesImage = (collection: HighlightCollections[number]) => {
  const firstSeriesImg =
    collection.collectionType === _CollectionType.Series
      ? collection.seriesImages?.urls[0]
      : undefined;
  const seriesImg = collection.seriesDetails?.logoUrl ?? firstSeriesImg;
  return seriesImg;
};

export type SelectedHihglightCondition = {
  collection?: HighlightCollections[number];
  token?: TokenDetails;
  condition?: Condition;
};

export default function CreatedOnHighlightTab(props: {
  onSelected: (item?: SelectedHihglightCondition) => void;
  mode?: "COLLECTION" | "TOKEN" | "ATTRIBUTE";
  initialAddress?: string;
}) {
  const { mode = "COLLECTION", onSelected } = props;

  return (
    <Box>
      <Box mih={300}>
        {mode === "COLLECTION" && (
          <HighlightCollectionSelectionFlow onSelectCollection={onSelected} />
        )}

        {mode === "TOKEN" && (
          <HighlightTokenSelectionFlow onSelectToken={onSelected} />
        )}

        {mode === "ATTRIBUTE" && (
          <HighlightAttributeSelectionFlow onSelectAttribute={onSelected} />
        )}
      </Box>
    </Box>
  );
}

function HighlightCollectionSelectionFlow(props: {
  onSelectCollection: (data: SelectedHihglightCondition) => void;
}) {
  const { onSelectCollection } = props;

  const [selectedCollection, setSelectedCollection] =
    useState<SimpleInfiniteListItem<HighlightCollections[number]>>();

  const selectedCollectionDetails = useMemo<
    CustomCollectionDetails | undefined
  >(() => {
    const collectionData = selectedCollection?.data;
    if (collectionData == null) return;
    return extractCollectionDetails(collectionData);
  }, [selectedCollection]);

  const handleSelectCollection = () => {
    if (selectedCollectionDetails == null) return;

    const condition: Condition = {
      type: ConditionType.NFT,
      amount: 1,
      data: {
        network: selectedCollectionDetails.network,
        address: selectedCollectionDetails.address || "",
        name: selectedCollectionDetails.name || "",
        collectionName: selectedCollectionDetails.name || "",
        imageUrl: selectedCollectionDetails.imageUrl,
        standard: selectedCollectionDetails.standard,
      },
    };

    // TODO (jj) why was there collection?.id passed back?
    onSelectCollection({
      collection: selectedCollection?.data,
      condition,
    });
  };

  return (
    <Box>
      <HighlightCollectionGrid
        selectedCollection={selectedCollection}
        onSelectCollection={setSelectedCollection}
      />

      <Space h="sm" />

      <BottomModalSection>
        <Button
          disabled={selectedCollection?.data == null}
          size="xl"
          fullWidth
          onClick={handleSelectCollection}
        >
          Select collection
        </Button>
      </BottomModalSection>
    </Box>
  );
}

function HighlightTokenSelectionFlow(props: {
  onSelectToken: (data: SelectedHihglightCondition) => void;
}) {
  const { onSelectToken } = props;
  const [step, setStep] = useState(0);
  const [selectedCollection, setSelectedCollection] =
    useState<SimpleInfiniteListItem<HighlightCollections[number]>>();
  const [selectedToken, setSelectedToken] =
    useState<SimpleInfiniteListItem<ContractNft>>();

  const collectionNetwork = useMemo(() => {
    if (selectedCollection?.data == null) return null;

    return networkLookup(selectedCollection.data.chainId);
  }, [selectedCollection]);

  const { data: contractCollection } = useContractDetailsQuery({
    variables: {
      network: collectionNetwork?.type ?? "",
      address: selectedCollection?.data?.contract?.address ?? "",
    },
    skip: collectionNetwork == null,
  });

  const handleSelectToken = () => {
    const tokenData = selectedToken?.data;

    if (selectedCollectionDetails == null) return;
    if (tokenData == null) return;

    const condition: Condition = {
      type: ConditionType.NFT,
      amount: 1,
      data: {
        network: selectedCollectionDetails.network,
        address: selectedCollectionDetails.address || "",
        name: tokenData?.name || selectedCollectionDetails.name || "",
        collectionName: selectedCollectionDetails.name || "",
        imageUrl: selectedCollectionDetails.imageUrl,
        videoUrl: tokenData.videoUrl || "",
        standard: selectedCollectionDetails.standard,
        tokenId: tokenData.id,
      },
    };

    onSelectToken({
      collection: selectedCollection?.data,
      condition,
    });
  };

  const selectedCollectionDetails = useMemo<
    CustomCollectionDetails | undefined
  >(() => {
    const collectionData = selectedCollection?.data;
    if (collectionData == null) return;
    return extractCollectionDetails(collectionData);
  }, [selectedCollection]);

  return (
    <Box>
      {step === 0 && (
        <>
          <HighlightCollectionGrid
            selectedCollection={selectedCollection}
            onSelectCollection={setSelectedCollection}
          />

          <Space h="sm" />

          <BottomModalSection>
            <Button
              disabled={selectedCollection?.data == null}
              size="xl"
              fullWidth
              onClick={() => setStep(1)}
            >
              Select collection
            </Button>
          </BottomModalSection>
        </>
      )}

      {step === 1 && (
        <>
          <CollectionDetailsSection
            network={collectionNetwork?.type}
            collection={selectedCollectionDetails}
            onClear={() => {
              setSelectedCollection(undefined);
              setSelectedToken(undefined);
              setStep(0);
            }}
          />

          {contractCollection?.contractDetails != null &&
            collectionNetwork != null && (
              <>
                <Space h={"md"} />

                <ContractCollectionTokenGrid
                  contractCollection={contractCollection.contractDetails}
                  network={collectionNetwork.type}
                  selectedToken={selectedToken}
                  onSelectToken={setSelectedToken}
                />
              </>
            )}

          <Space h="sm" />

          <BottomModalSection>
            <Button
              disabled={selectedToken?.data == null}
              size="xl"
              fullWidth
              onClick={handleSelectToken}
            >
              {"Select token" +
                ((selectedToken?.data?.id &&
                  ` ( ${selectedToken?.data?.id} )`) ||
                  "")}
            </Button>
          </BottomModalSection>
        </>
      )}
    </Box>
  );
}

function HighlightAttributeSelectionFlow(props: {
  onSelectAttribute: (data: SelectedHihglightCondition) => void;
}) {
  const { onSelectAttribute } = props;
  const [step, setStep] = useState(0);

  const [selectedCollection, setSelectedCollection] =
    useState<SimpleInfiniteListItem<HighlightCollections[number]>>();
  const [selectedAttribute, setSelectedAttribute] =
    useState<TokenGridAttribute>();

  const collectionNetwork = useMemo(() => {
    if (selectedCollection?.data == null) return null;

    return networkLookup(selectedCollection.data.chainId);
  }, [selectedCollection]);

  const { data: contractCollection } = useContractDetailsQuery({
    variables: {
      network: collectionNetwork?.type ?? "",
      address: selectedCollection?.data?.contract?.address ?? "",
    },
    skip: collectionNetwork == null,
  });

  const handleSelectAttribute = () => {
    if (selectedAttribute == null) return;
    if (selectedCollectionDetails == null) return;

    const condition: Condition = {
      type: ConditionType.ATTRIBUTE,
      amount: 1,
      data: {
        network: selectedCollectionDetails.network,
        address: selectedCollectionDetails.address || "",
        name: selectedCollectionDetails.name || "",
        collectionName: selectedCollectionDetails.name || "",
        imageUrl: selectedCollectionDetails.imageUrl,
        standard: selectedCollectionDetails.standard,
        attributes: [
          {
            key: selectedAttribute.traitType,
            value: [selectedAttribute.traitValue],
          },
        ],
      },
    };

    onSelectAttribute({
      collection: selectedCollection?.data,
      condition,
    });
  };

  const selectedCollectionDetails = useMemo<
    CustomCollectionDetails | undefined
  >(() => {
    const collectionData = selectedCollection?.data;
    if (collectionData == null) return;
    return extractCollectionDetails(collectionData);
  }, [selectedCollection]);

  return (
    <Box>
      {step === 0 && (
        <>
          <HighlightCollectionGrid
            selectedCollection={selectedCollection}
            onSelectCollection={setSelectedCollection}
          />

          <Space h="sm" />

          <BottomModalSection>
            <Button
              disabled={selectedCollection?.data == null}
              size="xl"
              fullWidth
              onClick={() => setStep(1)}
            >
              Continue to select attribute
            </Button>
          </BottomModalSection>
        </>
      )}

      {step === 1 && (
        <>
          <CollectionDetailsSection
            network={collectionNetwork?.type}
            collection={selectedCollectionDetails}
            onClear={() => {
              setSelectedCollection(undefined);
              setSelectedAttribute(undefined);
              setStep(0);
            }}
          />

          {contractCollection?.contractDetails != null &&
            collectionNetwork != null && (
              <>
                <Space h={"md"} />

                <ContractCollectionAttributeGrid
                  network={collectionNetwork.type}
                  contract={contractCollection.contractDetails.contract}
                  selectedAttribute={selectedAttribute}
                  onSelectAttribute={setSelectedAttribute}
                />
              </>
            )}

          <Space h="sm" />

          <BottomModalSection>
            <Button
              disabled={selectedAttribute == null}
              size="xl"
              fullWidth
              onClick={handleSelectAttribute}
            >
              Select attribute
            </Button>
          </BottomModalSection>
        </>
      )}
    </Box>
  );
}

function extractCollectionDetails(
  collection: HighlightCollections[number]
): CustomCollectionDetails | undefined {
  if (collection == null) return;

  const network = networkLookup(collection?.chainId ?? "1").type;

  const image =
    (collection?.editions && collection.editions[0].image) ||
    (collection?.collectionType == _CollectionType.Series &&
      pickSeriesImage(collection)) ||
    collection?.importData?.media ||
    "";

  const standard =
    collection?.type === CollectionContractType.Imported
      ? collection?.importData?.standard
      : "ERC721";

  return {
    network,
    address: collection.contract?.address ?? undefined,
    imageUrl: image,
    name: collection.name || "unknown",
    standard: standard ?? "",
    items: "300",
  };
}
